/**
 * Model resolution utilities for agent providers.
 *
 * Pure functions that map model aliases to API model IDs and derive
 * provider IDs from model strings.
 *
 * @module @reflex/plugin-api/providers/internals/model-resolver
 */

import { ChatModelSchema } from '@reflex/shared';
import { getModelId } from '../../models.js';

/**
 * Resolve a raw model string to the concrete API model ID.
 *
 * If the input is a valid chat model alias (e.g., 'sonnet', 'opus', 'haiku'),
 * maps it to the corresponding API model ID via the model registry. Otherwise,
 * returns the raw string unchanged (pass-through for direct model IDs like
 * 'claude-sonnet-4-20250514' or 'openai/gpt-4o').
 *
 * Provider plugins that handle models in a different format (e.g., OpenCode's
 * `provider/model` syntax) should bypass this by overriding
 * `BaseAgentProvider.resolveModelId()`.
 */
export function resolveModelId(rawModel: string): string {
  const parsed = ChatModelSchema.safeParse(rawModel);
  return parsed.success ? getModelId(parsed.data) : rawModel;
}

/**
 * Derive the provider ID for a given model ID using heuristics.
 *
 * Note: Discovered models carry their `providerId` explicitly; this fallback
 * covers legacy scenarios or models not yet in discovery. Prefer using
 * the providerId from LaunchPreflightResult when available.
 *
 * Heuristic order:
 * 1. If model contains `/`, extract prefix as provider (e.g., 'openai/gpt-4o' → 'openai')
 * 2. If model is 'bigpickle', return 'opencode'
 * 3. If model starts with 'gpt', 'o1', 'o3', or 'chatgpt', return 'openai'
 * 4. Default to 'anthropic'
 */
export function getProviderForModel(modelId: string): string {
  const slashIdx = modelId.indexOf('/');
  if (slashIdx > 0) {
    return modelId.substring(0, slashIdx);
  }

  const lowerModelId = modelId.toLowerCase();

  if (lowerModelId === 'bigpickle') {
    return 'opencode';
  }

  if (
    lowerModelId.startsWith('gpt') ||
    lowerModelId.startsWith('o1') ||
    lowerModelId.startsWith('o3') ||
    lowerModelId.startsWith('chatgpt')
  ) {
    return 'openai';
  }

  return 'anthropic';
}
